import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

// World map topology URL - using world-atlas with proper ISO codes
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface CountryData {
  code: string;
  name: string;
  visits: number;
}

interface WorldMapProps {
  data: CountryData[];
}

// Mapping from alpha-2 to alpha-3 country codes for react-simple-maps
const countryCodeMap: Record<string, string> = {
  'US': 'USA',
  'DE': 'DEU',
  'CN': 'CHN',
  'IR': 'IRN',
  'TR': 'TUR',
  'GB': 'GBR',
  'FR': 'FRA',
  'IT': 'ITA',
  'ES': 'ESP',
  'JP': 'JPN',
  'KR': 'KOR',
  'IN': 'IND',
  'BR': 'BRA',
  'RU': 'RUS',
  'AU': 'AUS',
  'CA': 'CAN',
  'MX': 'MEX',
  'AR': 'ARG',
  'ZA': 'ZAF',
  'EG': 'EGY',
  'SA': 'SAU',
  'AE': 'ARE',
  'IL': 'ISR',
  'TH': 'THA',
  'VN': 'VNM',
  'MY': 'MYS',
  'ID': 'IDN',
  'PH': 'PHL',
  'PK': 'PAK',
  'BD': 'BGD',
  'NG': 'NGA',
  'KE': 'KEN',
  'GH': 'GHA',
  'MA': 'MAR',
  'TN': 'TUN',
  'DZ': 'DZA',
  'PL': 'POL',
  'CZ': 'CZE',
  'HU': 'HUN',
  'RO': 'ROU',
  'BG': 'BGR',
  'GR': 'GRC',
  'PT': 'PRT',
  'NL': 'NLD',
  'BE': 'BEL',
  'CH': 'CHE',
  'AT': 'AUT',
  'SE': 'SWE',
  'NO': 'NOR',
  'DK': 'DNK',
  'FI': 'FIN',
  'IE': 'IRL',
  'NZ': 'NZL',
  'SG': 'SGP',
  'HK': 'HKG',
  'TW': 'TWN'
};

export const WorldMap: React.FC<WorldMapProps> = ({ data }) => {
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  // Find the maximum visits for color scaling
  const maxVisits = Math.max(...data.map(country => country.visits), 1);

  const getCountryColor = (geo: any) => {
    // Try to match by country name first (more reliable)
    const geoName = geo.properties.NAME || geo.properties.name;
    const country = data.find(c =>
      c.name.toLowerCase() === geoName?.toLowerCase() ||
      geoName?.toLowerCase().includes(c.name.toLowerCase()) ||
      c.name.toLowerCase().includes(geoName?.toLowerCase())
    );

    // Debug: log successful matches
    if (country && country.visits > 0) {
      console.log(`Matched by name: "${geoName}" -> ${country.name} (${country.code}): ${country.visits} visits`);
    }

    if (country && country.visits > 0) {
      // Calculate color intensity based on visits relative to max visits
      const intensity = Math.max(0.1, Math.min(1, country.visits / maxVisits));

      // Generate a blue gradient from light to dark
      const r = Math.round(37 + (99 - 37) * (1 - intensity)); // 37 to 99
      const g = Math.round(99 + (235 - 99) * (1 - intensity)); // 99 to 235
      const b = Math.round(235 - (235 - 37) * (1 - intensity)); // 235 to 37

      return `rgb(${r}, ${g}, ${b})`;
    }

    // Fallback: try ISO codes if name matching fails
    const alpha3Code = geo.properties.ISO_A3 || geo.properties.ADM0_A3 || geo.properties.ISO_A3_EH;
    const alpha2Code = Object.keys(countryCodeMap).find(
      key => countryCodeMap[key] === alpha3Code
    );
    const countryByCode = data.find(c => c.code === alpha2Code);

    if (countryByCode && countryByCode.visits > 0) {
      const intensity = Math.max(0.1, Math.min(1, countryByCode.visits / maxVisits));
      const r = Math.round(37 + (99 - 37) * (1 - intensity));
      const g = Math.round(99 + (235 - 99) * (1 - intensity));
      const b = Math.round(235 - (235 - 37) * (1 - intensity));
      return `rgb(${r}, ${g}, ${b})`;
    }

    return '#f3f4f6'; // Light gray for countries with no data
  };

  const getCountryData = (geo: any) => {
    // Try to match by country name first
    const geoName = geo.properties.NAME || geo.properties.name;
    const country = data.find(c =>
      c.name.toLowerCase() === geoName?.toLowerCase() ||
      geoName?.toLowerCase().includes(c.name.toLowerCase()) ||
      c.name.toLowerCase().includes(geoName?.toLowerCase())
    );

    if (country) return country;

    // Fallback: try ISO codes
    const alpha3Code = geo.properties.ISO_A3 || geo.properties.ADM0_A3 || geo.properties.ISO_A3_EH;
    const alpha2Code = Object.keys(countryCodeMap).find(
      key => countryCodeMap[key] === alpha3Code
    );
    return data.find(c => c.code === alpha2Code);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (tooltipContent) {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  return (
    <div className="w-full h-full relative overflow-hidden" onMouseMove={handleMouseMove}>
      <ComposableMap
        projection="geoNaturalEarth1"
        projectionConfig={{
          scale: 120,
          center: [0, 0]
        }}
        width={800}
        height={400}
        style={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%'
        }}
        className="w-full h-full"
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const countryData = getCountryData(geo);

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={getCountryColor(geo)}
                  stroke="#ffffff"
                  strokeWidth={0.5}
                  style={{
                    default: {
                      outline: 'none',
                    },
                    hover: {
                      outline: 'none',
                      fill: countryData ? '#3b82f6' : '#f3f4f6',
                      cursor: 'pointer',
                    },
                    pressed: {
                      outline: 'none',
                    },
                  }}
                  onMouseEnter={(event) => {
                    const countryData = getCountryData(geo);
                    if (countryData) {
                      setTooltipContent(`${countryData.name}: ${countryData.visits.toLocaleString()} visits`);
                    } else {
                      setTooltipContent(`${geo.properties.NAME || 'Unknown'}: No data`);
                    }
                    setTooltipPosition({ x: event.clientX, y: event.clientY });
                  }}
                  onMouseLeave={() => {
                    setTooltipContent('');
                    setTooltipPosition(null);
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {/* Custom Tooltip */}
      {tooltipContent && tooltipPosition && (
        <div
          className="fixed z-50 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-sm font-medium text-gray-700 pointer-events-none max-w-xs break-words"
          style={{
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y - 10,
            transform: 'translate(0, -100%)',
          }}
        >
          {tooltipContent}
        </div>
      )}
    </div>
  );
};